import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtINR, today } from "@/lib/format";
import { toast } from "sonner";
import { FileCheck2, ArrowRight, Package } from "lucide-react";
import { Link } from "react-router-dom";

const fyStart = () => { const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1; return `${y - 1}-04-01`; };

const KPI = ({ label, value, sub, tone = "default", to }) => {
  const inner = (
    <div className="card-sharp p-5 h-full transition-transform hover:-translate-y-0.5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`metric-value text-2xl mt-2 ${tone === "positive" ? "text-emerald-600" : tone === "negative" ? "text-red-600" : tone === "warning" ? "text-amber-600" : ""}`}>{value}</div>
      {sub && <div className="text-xs mt-1 text-muted-foreground">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

export default function O2CCompliance() {
  const [fromDate, setFromDate] = useState(fyStart());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const r = await api.get("/reports/o2c-compliance", { params: { from_date: fromDate, to_date: toDate } });
      setData(r.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadFilingPack = async () => {
    try {
      const r = await api.get("/reports/filing-pack.zip", {
        params: { from_date: fromDate, to_date: toDate }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Filing_Pack_${fromDate}_to_${toDate}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Filing pack downloaded — hand to your CA");
    } catch (e) { toast.error("Download failed"); }
  };

  if (!data) return <div className="p-10 text-muted-foreground">Loading compliance summary…</div>;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="o2c-compliance-page">
      <PageHeader eyebrow="O2C" title="Compliance Summary"
        description="GST · TDS · TCS single-pane compliance snapshot for the selected period. Every card is drill-through to its detail."
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40 rounded-sm h-9" data-testid="o2c-from" />
            <span className="text-muted-foreground">→</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40 rounded-sm h-9" data-testid="o2c-to" />
            <Button variant="outline" className="rounded-sm h-9" onClick={load} data-testid="o2c-refresh">Refresh</Button>
            <Button className="rounded-sm h-9 bg-accent text-white gap-1.5" onClick={downloadFilingPack} data-testid="o2c-filing-pack">
              <Package className="w-3.5 h-3.5" /> Filing Pack
            </Button>
          </div>
        } />

      <Tabs defaultValue="gst">
        <TabsList className="rounded-sm">
          <TabsTrigger value="gst" className="rounded-sm" data-testid="o2c-tab-gst">GST</TabsTrigger>
          <TabsTrigger value="tds" className="rounded-sm" data-testid="o2c-tab-tds">TDS</TabsTrigger>
          <TabsTrigger value="tcs" className="rounded-sm" data-testid="o2c-tab-tcs">TCS</TabsTrigger>
          <TabsTrigger value="ar" className="rounded-sm" data-testid="o2c-tab-ar">Receivables</TabsTrigger>
        </TabsList>

        <TabsContent value="gst" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPI label="GST Output (Sales)" value={fmtINR(data.gst.output.total)} sub={`${data.gst.output.invoice_count} invoices`} tone="warning" to="/app/sales" />
            <KPI label="GST Input (Purchases)" value={fmtINR(data.gst.input.total)} sub={`${data.gst.input.bill_count} bills`} tone="positive" to="/app/purchases" />
            <KPI label="Net GST Payable" value={fmtINR(data.gst.net_payable)} sub={data.gst.net_payable > 0 ? "Cash outflow due" : "—"} tone="negative" />
            <KPI label="Net GST Credit" value={fmtINR(data.gst.net_credit)} sub={data.gst.net_credit > 0 ? "Carried forward" : "—"} tone="positive" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-sharp overflow-hidden">
              <div className="px-4 py-3 border-b text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Output tax breakup</div>
              <table className="w-full dense">
                <thead><tr><th>Component</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  <tr><td>CGST</td><td className="text-right tabular-nums">{fmtINR(data.gst.output.cgst)}</td></tr>
                  <tr><td>SGST</td><td className="text-right tabular-nums">{fmtINR(data.gst.output.sgst)}</td></tr>
                  <tr><td>IGST</td><td className="text-right tabular-nums">{fmtINR(data.gst.output.igst)}</td></tr>
                  <tr className="bg-blue-50 font-semibold"><td>Total output GST</td><td className="text-right tabular-nums">{fmtINR(data.gst.output.total)}</td></tr>
                  <tr><td className="text-xs text-muted-foreground">Taxable value</td><td className="text-right tabular-nums text-xs">{fmtINR(data.gst.output.taxable_value)}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="card-sharp overflow-hidden">
              <div className="px-4 py-3 border-b text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Input tax credit</div>
              <table className="w-full dense">
                <thead><tr><th>Component</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  <tr><td>CGST</td><td className="text-right tabular-nums">{fmtINR(data.gst.input.cgst)}</td></tr>
                  <tr><td>SGST</td><td className="text-right tabular-nums">{fmtINR(data.gst.input.sgst)}</td></tr>
                  <tr><td>IGST</td><td className="text-right tabular-nums">{fmtINR(data.gst.input.igst)}</td></tr>
                  <tr className="bg-emerald-50 font-semibold"><td>Total input GST (ITC)</td><td className="text-right tabular-nums">{fmtINR(data.gst.input.total)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tds" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KPI label="Total TDS Deducted" value={fmtINR(data.tds.total_deducted)} sub={`Across ${data.tds.by_section.length} sections`} tone="warning" />
            <KPI label="Sections used" value={data.tds.by_section.length} sub="Distinct IT Act sections" />
          </div>
          <div className="card-sharp overflow-hidden">
            <div className="px-4 py-3 border-b text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Section-wise TDS breakdown</div>
            <table className="w-full dense">
              <thead><tr><th>Section</th><th>Count</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {data.tds.by_section.length === 0 && <tr><td colSpan="3" className="text-center py-6 text-muted-foreground">No TDS deducted this period</td></tr>}
                {data.tds.by_section.map((s) => (
                  <tr key={s.section}><td className="font-mono">{s.section}</td><td>{s.count}</td><td className="text-right tabular-nums">{fmtINR(s.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="tcs" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KPI label="Total TCS Collected" value={fmtINR(data.tcs.total_collected)} sub={`${data.tcs.by_section.length} sections`} tone="positive" />
            <KPI label="Sections used" value={data.tcs.by_section.length} />
          </div>
          <div className="card-sharp overflow-hidden">
            <div className="px-4 py-3 border-b text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Section-wise TCS breakdown</div>
            <table className="w-full dense">
              <thead><tr><th>Section</th><th>Count</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {data.tcs.by_section.length === 0 && <tr><td colSpan="3" className="text-center py-6 text-muted-foreground">No TCS collected this period</td></tr>}
                {data.tcs.by_section.map((s) => (
                  <tr key={s.section}><td className="font-mono">{s.section}</td><td>{s.count}</td><td className="text-right tabular-nums">{fmtINR(s.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ar" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KPI label="Invoices overdue > 30 days" value={data.receivables.overdue_30_plus_count} sub="Click to open the customer register" tone="negative" to="/app/customers" />
            <KPI label="Compliance stance" value={data.receivables.overdue_30_plus_count === 0 ? "Clean" : "At risk"} tone={data.receivables.overdue_30_plus_count === 0 ? "positive" : "negative"} />
          </div>
          <div className="card-sharp p-5">
            <div className="text-sm">Follow-up recommendations:</div>
            <ul className="text-xs text-muted-foreground mt-2 list-disc pl-5 space-y-1">
              <li>Send auto-reminder emails 3/7/15 days before due date.</li>
              <li>Escalate accounts &gt; 60 days overdue to Sales manager.</li>
              <li>Book bad-debt provisions per Ind AS 109 for accounts &gt; 180 days.</li>
            </ul>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
